import React, { useEffect, useState } from 'react';
import Icon from '../../../hive/ui/Icon';
import { useToast } from '../ui';
import { SupplierSheet, ShortlistSheets, FairSheets } from './sheets';
import { exportSupplierPdf, exportShortlistPdf, exportFairPdf } from './pdf';
import './fairTrackerReports.css';
import { tr } from '../i18n';

/**
 * In-app report viewer (spec § 8.1).
 *
 * Navy top bar, grey canvas, white A4-like sheets, black bottom bar with
 * Print / Download / Share.
 *
 * THE THREE BUTTONS DO THREE DIFFERENT THINGS, on purpose:
 *
 *   Print    — the browser renders these HTML sheets, so "Save as PDF" from
 *              the print dialog gives a true vector PDF with Chinese set in
 *              real fonts. The best output, and the one to use when the file
 *              is going to a Chinese supplier.
 *   Download — jsPDF, in `pdf.js`. Fast, small and selectable, and it SAVES
 *              rather than opening a share sheet (it used to do the latter,
 *              making it indistinguishable from Share). Chinese strings are
 *              rasterised individually there, since jsPDF's built-in fonts
 *              are Latin-only.
 *   Share    — the same jsPDF output handed to the Web Share sheet, falling
 *              back to a message when the platform cannot share files.
 *
 * Download and Print are built by DIFFERENT code paths, so a change to what a
 * report contains has to be made in both `sheets.jsx` and `pdf.js`. They are
 * kept to the same facts in the same order deliberately; see supplierBody().
 */
export default function ReportViewer({ kind, supplier, suppliers, fair, onClose }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // The viewer is a full-screen overlay; stop the page behind it scrolling.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const title =
    kind === 'supplier'
      ? `${(supplier && supplier.company) || 'Supplier'}.pdf`
      : kind === 'shortlist'
      ? `Final shortlist — ${(fair && fair.name) || 'fair'}.pdf`
      : `${(fair && fair.name) || 'Fair'} report.pdf`;

  const safe = (t) => String(t || '').replace(/[\\/:*?"<>|]+/g, '-').trim();
  const stamp = new Date().toISOString().slice(0, 10);
  const filename =
    kind === 'supplier'
      ? `BeeNexa_Supplier_${safe((supplier && supplier.company) || 'supplier')}.pdf`
      : kind === 'shortlist'
      ? `BeeNexa_Final_Shortlist_${safe((fair && fair.name) || 'fair')}_${stamp}.pdf`
      : `BeeNexa_Fair_Report_${safe((fair && fair.name) || 'fair')}_${stamp}.pdf`;

  const download = async () => {
    setBusy(true);
    try {
      if (kind === 'supplier') await exportSupplierPdf({ supplier, fair, filename });
      else if (kind === 'shortlist') await exportShortlistPdf({ suppliers, fair, filename });
      else await exportFairPdf({ suppliers, fair, filename });
      toast('PDF downloaded');
    } catch (e) {
      toast('Could not build the PDF', 'error');
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const blob =
        kind === 'supplier'
          ? await exportSupplierPdf({ supplier, fair, filename, asBlob: true })
          : kind === 'shortlist'
          ? await exportShortlistPdf({ suppliers, fair, filename, asBlob: true })
          : await exportFairPdf({ suppliers, fair, filename, asBlob: true });

      const file = new File([blob], filename, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title });
      } else {
        toast('Sharing is not available here — use Download', 'error');
      }
    } catch (e) {
      if (!e || e.name !== 'AbortError') toast('Could not share', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ft-viewer" role="dialog" aria-modal="true" aria-label={title}>
      <header className="ft-viewer-bar">
        <button type="button" className="ft-icon-btn ft-on-dark" onClick={onClose} aria-label={tr("Close")}>
          <Icon name="ChevronLeft" size={20} />
        </button>
        <span>{title}</span>
      </header>

      <div className="ft-viewer-canvas ft-scroll">
        {/* The old "this report contains Chinese, use Print" warning is gone:
            Download now renders CJK rather than dropping it. Print is still
            the better output for Chinese — it is vector — but no longer the
            only one that works. */}

        <div id="ft-print-root">
          {kind === 'supplier' && <SupplierSheet supplier={supplier} fair={fair} />}
          {kind === 'shortlist' && <ShortlistSheets suppliers={suppliers} fair={fair} />}
          {kind === 'fair' && <FairSheets suppliers={suppliers} fair={fair} />}
        </div>
      </div>

      <footer className="ft-viewer-foot">
        <button type="button" className="ft-btn ft-btn-ink" onClick={() => window.print()}>
          <Icon name="Printer" size={16} /> {tr("Print")}
        </button>
        <button type="button" className="ft-btn ft-btn-ink" disabled={busy} onClick={download}>
          <Icon name="Download" size={16} /> {tr('Download')}
        </button>
        <button type="button" className="ft-btn ft-btn-ink" disabled={busy} onClick={share}>
          <Icon name="Share2" size={16} /> {tr("Share")}
        </button>
      </footer>
    </div>
  );
}
